import { AMEND } from '@/src/constants/itinerary/fareFamily'
import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { Charge, FareFamilyFareRule, ProcessedTimeline, Solution } from '@/src/types/fareFamilyInfo'
import { FareBenefit } from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { useTranslations } from 'next-intl'
import useFareFamilyInfoApiCall from '../../../useFareFamilyInfoApiCall'

const useProcessBenefits = ({
    benefits,
    fareBenefits,
    ctBrandName
}: {
    benefits: FareBenefit[]
    fareBenefits: FareBenefit[]
    ctBrandName: string
}) => {
    const itineraryId = useGetItineraryId()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const t = useTranslations('fareFamily')

    const {
        data: fareFamilyInfoData,
        isLoading,
        isFetching
    } = useFareFamilyInfoApiCall({
        selectFromResult: ({ data, isLoading, isFetching }) => ({
            data,
            isLoading,
            isFetching
        })
    })
    const isMiniRulesDataLoading = isFetching || isLoading

    // const infoNotAvailableValues = [
    //     t('Info unavailable'),
    //     t('Info not available'),
    //     t('Not available'),
    //     t('Not allowed'),
    //     t('Not Allowed'),
    //     t('Non changeable'),
    //     t('Non refundable')
    // ]

    const infoNotAvailableValues: string[] = []

    const { totalDateChangeFee, isAmendable, isRefundable, refundableAmountOnCancellation } =
        fareFamilyInfoData?.data || {}

    const getTooltipDataForCard = (charges: Charge[], type: string) => {
        let chargeTimelines: ProcessedTimeline[] = []
        let notPermittedTextValue = type === AMEND ? t('Non changeable') : t('Non refundable')
        if (Array.isArray(charges)) {
            chargeTimelines = charges
                .map((chargeTimeline, index) => {
                    const price =
                        type === AMEND ? chargeTimeline?.amount?.totalCharge : chargeTimeline?.amount?.amountRefundable
                    const { timeline, permitted } = chargeTimeline || {}
                    const { endTime, startTime } = timeline || {}
                    if (startTime && endTime && !isEmpty(price)) {
                        return {
                            startTime,
                            endTime,
                            price,
                            permitted,
                            notPermittedTextValue
                        }
                    }
                })
                .filter(Boolean) as ProcessedTimeline[]
        }
        return chargeTimelines
    }

    const getMiniruleData = ({
        ctBrandName,
        benefitKey,
        miniRulesData
    }: {
        ctBrandName: string
        benefitKey: string
        miniRulesData: Solution[]
    }) => {
        let tooltipContent: { title: string; content: ProcessedTimeline[] } = {
            title: '',
            content: []
        }
        try {
            if (Array.isArray(miniRulesData)) {
                const [onwardData] = miniRulesData || []
                const {
                    fareFamilyFareRules: onwardFareRules = {},
                    originDestinationInfo: onwardOriginDestinationInfo = {}
                } = onwardData || {}
                const selectedFareMiniRulesDataOnward = onwardFareRules?.[ctBrandName]?.fareRules || []
                const onwardBenefitData =
                    selectedFareMiniRulesDataOnward?.find(rule => rule?.type === benefitKey) ||
                    ({} as FareFamilyFareRule)
                if (isEmpty(onwardBenefitData)) {
                    return tooltipContent
                }
                const { charges: onwardCharges = [], type: onwardType } = onwardBenefitData || {}
                const onwardTooltipData = !isEmpty(onwardCharges)
                    ? getTooltipDataForCard(onwardCharges, onwardType)
                    : []

                if (!isEmpty(onwardTooltipData)) {
                    tooltipContent = {
                        title: benefitKey === 'CANCEL' ? t('refundAmountOnCancellation') : t('dateChangeFee'),
                        content: onwardTooltipData
                    }
                }
                return tooltipContent
            }
            return tooltipContent
        } catch (ex) {
            return tooltipContent
        }
    }

    const processedBenefits = benefits.map((fareBenefit, benefitIndex, arr) => {
        let selectedBenefit: FareBenefit = {} as FareBenefit
        if (fareBenefits && !isEmpty(fareBenefits)) {
            selectedBenefit = fareBenefits.find(
                (benefit: FareBenefit) => benefit.benefitKey === fareBenefit.benefitKey
            ) as FareBenefit
        }
        const { description, benefitKey, collapsedBenefitDisplayName } = selectedBenefit || {}
        let displayText = ''
        let miniRuleDisplayValue = ''
        let tooltipContent: { title: string; content: ProcessedTimeline[] } = {
            title: '',
            content: []
        }

        // const benefitValue = deviceType === MOBILE ? collapsedBenefitDisplayName : description

        if (benefitKey === 'AMEND' && !isMiniRulesDataLoading) {
            if (!isEmpty(isAmendable) && !isAmendable?.[ctBrandName]) {
                miniRuleDisplayValue = t('Non changeable')
            } else if (!isEmpty(totalDateChangeFee?.[ctBrandName])) {
                miniRuleDisplayValue = description?.replace(
                    '{amount}',
                    formatCurrency(totalDateChangeFee?.[ctBrandName]!)
                )
                tooltipContent = getMiniruleData({
                    ctBrandName,
                    benefitKey,
                    miniRulesData: fareFamilyInfoData?.data?.solutions || []
                })
            } else {
                miniRuleDisplayValue = t('Info not available')
            }
        }
        if (benefitKey === 'CANCEL' && !isMiniRulesDataLoading) {
            if (!isEmpty(isRefundable) && !isRefundable?.[ctBrandName]) {
                miniRuleDisplayValue = t('Non refundable')
            } else if (!isEmpty(refundableAmountOnCancellation?.[ctBrandName])) {
                miniRuleDisplayValue = description?.replace(
                    '{amount}',
                    formatCurrency(refundableAmountOnCancellation?.[ctBrandName]!)
                )
                tooltipContent = getMiniruleData({
                    ctBrandName,
                    benefitKey,
                    miniRulesData: fareFamilyInfoData?.data?.solutions || []
                })
            } else {
                miniRuleDisplayValue = t('Info not available')
            }
        }

        return {
            benefitIndex,
            selectedBenefit,
            benefitKey,
            displayText,
            miniRuleDisplayValue,
            description,
            tooltipContent
        }
    })
    return { processedBenefits, isMiniRulesDataLoading, infoNotAvailableValues }
}

export default useProcessBenefits
